import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { ScrollToTop } from '@/components/ScrollToTop';
import { lazy, Suspense } from 'react';

// Eagerly load homepage for fastest FCP
import HomePage from '@/pages/HomePage';

// Lazy load all other pages to reduce initial bundle size
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const BrowsePage = lazy(() => import('@/pages/BrowsePage'));
const ClassDetailPage = lazy(() => import('@/pages/ClassDetailPage'));
const CreateClassPage = lazy(() => import('@/pages/CreateClassPage'));
const EditClassPage = lazy(() => import('@/pages/EditClassPage'));
const ClassCreatedPage = lazy(() => import('@/pages/ClassCreatedPage'));
const ClassRegisteredPage = lazy(() => import('@/pages/ClassRegisteredPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const InstructorDashboardPage = lazy(() => import('@/pages/InstructorDashboardPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const PremiumPage = lazy(() => import('@/pages/PremiumPage'));
const InstructorPayoutsPage = lazy(() => import('@/pages/InstructorPayoutsPage'));
const MarketingToolsPage = lazy(() => import('@/pages/MarketingToolsPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const FAQsPage = lazy(() => import('@/pages/FAQsPage'));
const EarnMoneyPage = lazy(() => import('@/pages/EarnMoneyPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const SuggestionsPage = lazy(() => import('@/pages/SuggestionsPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const FindVenuePage = lazy(() => import('@/pages/FindVenuePage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const VenueDetailPage = lazy(() => import('@/pages/VenueDetailPage'));
const ReferRedirect = lazy(() => import('@/pages/ReferRedirect'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage'));

const queryClient = new QueryClient();

// App root component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
           <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/browse" element={<BrowsePage />} />
                  <Route path="/class/:id" element={<ClassDetailPage />} />
                  <Route path="/teach" element={<CreateClassPage />} />
                  <Route path="/class/:id/edit" element={<EditClassPage />} />
                  <Route path="/class/:id/created" element={<ClassCreatedPage />} />
                  <Route path="/class/:id/registered" element={<ClassRegisteredPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/instructor" element={<InstructorDashboardPage />} />
                  <Route path="/instructor/premium" element={<PremiumPage />} />
                  <Route path="/instructor/payouts" element={<InstructorPayoutsPage />} />
                  <Route path="/instructor/marketing" element={<MarketingToolsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faqs" element={<FAQsPage />} />
                  <Route path="/earn-money" element={<EarnMoneyPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/cookies" element={<CookiePolicyPage />} />
                  <Route path="/disclaimer" element={<DisclaimerPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/suggestions" element={<SuggestionsPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/find-a-venue" element={<FindVenuePage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/venue/:id" element={<VenueDetailPage />} />
                  <Route path="/refer/:code" element={<ReferRedirect />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;